/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Title } from '../../../../components/Typography/Title';
import { Toolbar } from '../../../../components/Toolbar';
import { Table } from '../../../../components/Table';
import { Container } from './styles';
import {
	Button,
	Dropdown,
	Form,
	Icon,
	Label,
	PaginationProps,
	Popup,
} from 'semantic-ui-react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { FilterGroup } from '../../../../components/Form/FilterGroup';
import { Controller, useForm } from 'react-hook-form';
import { useDeleteCompetence } from '../../hooks/useDeleteCompetence';
import { useFindCompetencies } from '../../hooks/useFindCompetencies';
import { FilterListCompetenciesProps } from '../../types/competence.type';
import { useQuery } from '@tanstack/react-query';
import { findAreaRequest } from '../../../areas/requests/findAreaRequest';
import { toDropdownItemProps } from '../../../../utils/toDropdownItemProps';
import { NoResultsTableInformation } from '../../../../components/TableNoDataFound';
import { useTranslation } from 'react-i18next';
import { TableHealderProps } from '../../../../types/table.types';
import { ModalConfirm } from '../../../../components/ModalConfirm';

const tableHeader: TableHealderProps[] = [
	{ name: 'page.competencies.area', width: undefined, textAlign: undefined },
	{
		name: 'page.competencies.competence',
		width: undefined,
		textAlign: undefined,
	},
	{ name: 'page.competencies.description', width: 8, textAlign: undefined },
	{ name: 'page.competencies.active', width: 2, textAlign: 'center' },
];

export function ListCompetencies() {
	// @ts-ignore
	const { t } = useTranslation();
	const [openModal, setOpenModal] = useState<boolean>(false);
	const [item, setItem] = useState<number>();

	const { control, handleSubmit, reset } = useForm<FilterListCompetenciesProps>(
		{
			defaultValues: {
				active: true,
			},
		}
	);
	const [page, setPage] = useState(1);
	const [filter, setFilter] = useState<FilterListCompetenciesProps>({
		active: true,
	});

	const navigate = useNavigate();
	const location = useLocation();

	const { listCompetencies } = useFindCompetencies({ page, filter });
	const { deleteCompetence } = useDeleteCompetence();

	const { data: areaOptions } = useQuery(['AREA_OPTIONS'], async () => {
		const response = await findAreaRequest({});

		return toDropdownItemProps(response._embedded.areas);
	});

	const onSubmitFilter = (data: FilterListCompetenciesProps) => {
		setFilter(data);
		setPage(1);
	};

	const handleResetFilter = () => {
		reset({
			areaId: '',
			title: '',
			description: '',
			active: true,
		});
		setFilter({
			areaId: undefined,
			title: undefined,
			description: undefined,
			active: true,
		});
	};

	const handlePageChange = (
		_: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
		data: PaginationProps
	) => {
		setPage(Number(data.activePage));
	};

	return (
		<Container>
			<Title>{t('title.competencies')}</Title>

			<Toolbar
				handleAdd={() => navigate(`${location.pathname}/create`)}
				filter={{
					content: (
						<>
							<FilterGroup>
								<Controller
									control={control}
									name="areaId"
									render={({ field: { value, onChange } }) => (
										<Form.Dropdown
											label={t('page.competencies.area')}
											selection
											options={areaOptions ?? []}
											value={value}
											onChange={(_, data) => onChange(data.value)}
										/>
									)}
								/>

								<Controller
									control={control}
									name="title"
									render={({ field: { value, onChange } }) => (
										<Form.Input
											label={t('page.competencies.competence')}
											value={value}
											onChange={onChange}
										/>
									)}
								/>

								<Controller
									control={control}
									name="description"
									render={({ field: { value, onChange } }) => (
										<Form.TextArea
											label={t('page.competencies.description')}
											value={value}
											onChange={onChange}
										/>
									)}
								/>
							</FilterGroup>

							<FilterGroup>
								<Controller
									control={control}
									name="active"
									render={({ field: { value, onChange } }) => (
										<Form.Radio
											toggle
											label={t('page.competencies.active')}
											checked={value}
											onChange={(_, data) => onChange(data.checked)}
										/>
									)}
								/>
							</FilterGroup>
						</>
					),
					onApply: handleSubmit(onSubmitFilter),
					onClear: handleResetFilter,
				}}
			></Toolbar>

			<span>
				<Table>
					<Table.Header>
						<Table.Row>
							<Table.HeaderCell content="ID" isIdColumn />
							{tableHeader.map(({ name, width, textAlign }) => (
								<Table.HeaderCell
									key={name}
									width={width}
									textAlign={textAlign}
								>
									{t(name as any)}
								</Table.HeaderCell>
							))}

							<Table.HeaderCell isActionColumn>
								<Icon name="cog" size="large" />
							</Table.HeaderCell>
						</Table.Row>
					</Table.Header>

					<Table.Body>
						{listCompetencies?._embedded?.competencies?.map(
							({ id, area, title, description, active }, index) => (
								<Table.Row key={id}>
									<Table.Cell content={index + 1} />
									<Table.Cell>{area?.name}</Table.Cell>
									<Table.Cell>{title}</Table.Cell>
									<Table.Cell>{description}</Table.Cell>
									<Table.Cell width={2} textAlign="center">
										<Label
											color={active ? 'green' : 'red'}
											content={t(
												`page.competencies.${active ? 'active' : 'inactive'}`
											)}
										/>
									</Table.Cell>
									<Table.Cell isActionColumn>
										<Dropdown
											pointing="right"
											trigger={<Icon name="ellipsis vertical" size="large" />}
											icon={null}
										>
											<Dropdown.Menu>
												<span className="actionColumn__buttonGroup">
													<Popup
														content={t('tooltip.edit')}
														trigger={
															<Button
																icon="pencil"
																size="mini"
																color="yellow"
																onClick={() =>
																	navigate(`${location.pathname}/update/${id}`)
																}
															/>
														}
													/>

													<Popup
														content={t('tooltip.delete')}
														trigger={
															<Button
																icon="trash"
																size="mini"
																color="red"
																onClick={() => {
																	setItem(id);
																	setOpenModal(true);
																}}
															/>
														}
													/>
												</span>
											</Dropdown.Menu>
										</Dropdown>
									</Table.Cell>
								</Table.Row>
							)
						)}
					</Table.Body>
				</Table>

				{listCompetencies?._embedded?.competencies?.length ? (
					<Table.Pagination
						page={page}
						activePage={page}
						totalPages={listCompetencies?.page.totalPages ?? 0}
						boundaryRange={1}
						siblingRange={0}
						onPageChange={handlePageChange}
						size="mini"
						firstItem={null}
						lastItem={null}
						secondary
					/>
				) : (
					<NoResultsTableInformation
						icon="dont"
						title={t('message.noDataFound.title')}
						subTitle={t('message.noDataFound.subTitle')}
					/>
				)}
			</span>

			<Outlet />

			<ModalConfirm
				open={openModal}
				onClose={() => setOpenModal(false)}
				onOpen={() => setOpenModal(true)}
				title={t('component.modal.header.confirmation')}
				description={t('component.modal.content.AreYouSureYouWantToDelete')}
				type="remove"
				size="mini"
				onConfirm={() => {
					item && deleteCompetence.mutateAsync(item);
					setOpenModal(false);
				}}
				onCancel={() => setOpenModal(false)}
			/>
		</Container>
	);
}
